import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "../styles/theme.css";
import Header from "../Components/Header.jsx";
import projectData from "../data/projectsData.json";
import { ExternalLink, Layers, Code2, ZoomIn, X } from "lucide-react";

export default function Projects() {
  const [selectedImg, setSelectedImg] = useState(null);
  const [activeStory, setActiveStory] = useState(null);
  const [filter, setFilter] = useState("all");

  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
    document.documentElement.setAttribute("data-theme", newTheme);
  };

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  return (
    <>
      <Header theme={theme} toggleTheme={toggleTheme} />
      <main className="page projects-page">
        
        {/* Page Header */}
        <header className="projects-header">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="section-header"
          >
            {/* <div className="tag">Portfolio</div> */}
            <h1 className="page-title">Selected <span>Works</span></h1>
            <p className="section-desc">
              A collection of digital experiences and visual identities built with 
              <b> React</b>, <b>PHP</b>, and <b>CorelDRAW</b>.
            </p>
          </motion.div>
          
          <div className="filter-nav glass">
            <button className={filter === "all" ? "active" : ""} onClick={() => setFilter("all")}>All Projects</button>
            <button className={filter === "web" ? "active" : ""} onClick={() => setFilter("web")}>Development</button>
            <button className={filter === "design" ? "active" : ""} onClick={() => setFilter("design")}>Graphic Design</button>
          </div>
        </header>

        {/* Web Engineering Section */}
        {(filter === "all" || filter === "web") && (
          <motion.section 
            initial={{ opacity: 0 }} 
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="project-section"
          >
            <div className="section-heading">
              <Code2 size={24} className="accent-icon" />
              <h2>Web Engineering</h2>
            </div>
            <div className="project-grid">
              {projectData.web.map((proj, index) => (
                <div key={index} className="project-card glass">
                  <a href={proj.link} target="_blank" rel="noreferrer" className="project-image-wrapper">
                    <img src={proj.preview} alt={proj.title} loading="lazy" />
                    <div className="image-instruction-overlay">
                       <ExternalLink size={24} />
                       <span>Visit Website</span>
                    </div>
                  </a>
                  <div className="card-content">
                    <div className="tech-stack">
                      {proj.tech.map(t => <span key={t} className="tech-tag">{t}</span>)}
                    </div>
                    <h3>{proj.title}</h3>
                    <p className="proj-desc">{proj.description}</p>
                    <button 
                      className="btn-story" 
                      onClick={() => setActiveStory(activeStory === `web-${index}` ? null : `web-${index}`)}
                    >
                      {activeStory === `web-${index}` ? "Close Story" : "Read Case Study"}
                    </button>
                    <AnimatePresence>
                      {activeStory === `web-${index}` && (
                        <motion.div 
                          initial={{ height: 0, opacity: 0 }} 
                          animate={{ height: "auto", opacity: 1 }} 
                          exit={{ height: 0, opacity: 0 }} 
                          className="story-text"
                        >
                          <p>{proj.story}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              ))}
            </div>
          </motion.section>
        )}

        {/* Design Section */}
        {(filter === "all" || filter === "design") && (
          <motion.section 
            initial={{ opacity: 0 }} 
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="project-section"
          >
            <div className="section-heading">
              <Layers size={24} className="accent-icon" />
              <h2>Visual Branding</h2>
            </div>
            <div className="project-grid">
              {projectData.design.map((proj, index) => (
                <div key={index} className="project-card glass">
                  <div className="project-image-wrapper pointer" onClick={() => setSelectedImg(proj.image)}>
                    <img src={proj.image} alt={proj.title} loading="lazy" />
                    <div className="image-instruction-overlay">
                       <ZoomIn size={28}/>
                       <span>Expand Design</span>
                    </div>
                  </div>
                  <div className="card-content">
                    <div className="tech-stack">
                      {proj.tags.map(t => <span key={t} className="tech-tag design">{t}</span>)}
                    </div>
                    <h3>{proj.title}</h3>
                    <button 
                      className="btn-story" 
                      onClick={() => setActiveStory(activeStory === `design-${index}` ? null : `design-${index}`)}
                    >
                      {activeStory === `design-${index}` ? "Hide Logic" : "Design Strategy"}
                    </button>
                    <AnimatePresence>
                      {activeStory === `design-${index}` && (
                        <motion.div 
                          initial={{ height: 0, opacity: 0 }} 
                          animate={{ height: "auto", opacity: 1 }} 
                          exit={{ height: 0, opacity: 0 }} 
                          className="story-text"
                        >
                          <p>{proj.story}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              ))}
            </div>
          </motion.section>
        )}

        {/* Lightbox Modal */}
        <AnimatePresence>
          {selectedImg && (
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }} 
              className="lightbox" 
              onClick={() => setSelectedImg(null)}
            >
              <button className="close-btn"><X size={32}/></button>
              <motion.img 
                initial={{ scale: 0.8, y: 20 }} 
                animate={{ scale: 1, y: 0 }} 
                src={selectedImg} 
                alt="High Resolution Preview" 
              />
            </motion.div>
          )}
        </AnimatePresence>
      </main>
   
    </>
  );
}